import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter38 — Landing.jsx is retired. This component sits at the app's root URL and
 * decides where the visitor should go:
 *
 *   ?kind=monthly|annual [+ ?offer=slug]  →  Stripe Checkout (via /billing/public-checkout)
 *   ?cancelled=1                          →  friendly "checkout cancelled" screen with a
 *                                            button back to the marketing site's pricing
 *   logged-in                             →  /dashboard
 *   everything else                       →  /auth (sign in)
 *
 * The marketing site (forecastly.me) is the single source of truth for pricing content.
 * The app's root URL is now purely a hand-off/redirect layer.
 */

// Where to send users after a cancelled checkout (or default "go home" nav).
// Overridable via env var so preview/prod can differ if needed.
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || "https://forecastly.me";

export default function PublicEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [checkoutError, setCheckoutError] = useState(null);
  const firedRef = useRef(false);

  const kind = searchParams.get("kind");
  const offer = searchParams.get("offer") || undefined;
  const cancelled = searchParams.get("cancelled") === "1";

  useEffect(() => {
    if (firedRef.current) return;

    // Cancelled checkout — stay on this page and show the message.
    if (cancelled) return;

    // Wait for auth check to settle before deciding where to send.
    if (loading) return;

    // Already signed in → dashboard.
    if (user) {
      firedRef.current = true;
      navigate("/dashboard", { replace: true });
      return;
    }

    // Marketing-site hand-off: `?kind=monthly|annual` → straight to Stripe.
    if (kind === "monthly" || kind === "annual") {
      firedRef.current = true;
      (async () => {
        try {
          const res = await axios.post(`${API}/billing/public-checkout`, {
            kind,
            origin_url: window.location.origin,
            campaign_slug: offer,
          });
          if (res.data?.url) {
            window.location.href = res.data.url;
            return;
          }
          setCheckoutError("Could not start checkout — please try again.");
        } catch (e) {
          const detail = e?.response?.data?.detail || "Could not start checkout — please try again.";
          setCheckoutError(detail);
          toast.error(detail);
        }
      })();
      return;
    }

    // Everything else → sign in.
    firedRef.current = true;
    navigate("/auth", { replace: true });
  }, [loading, user, kind, offer, cancelled, navigate]);

  // ---- Render states ----

  if (cancelled) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="public-entry-cancelled">
          <div className="text-4xl mb-4">👋</div>
          <h1 className="font-heading uppercase text-3xl mb-3">Checkout cancelled.</h1>
          <p className="text-black/70 mb-6 leading-relaxed">
            No worries — nothing was charged. If you'd like to take another look at the
            plans (or you had a promo offer you want to keep), head back to pricing.
          </p>
          <a
            href={`${MARKETING_URL}/pricing${offer ? `?offer=${encodeURIComponent(offer)}` : ""}`}
            className="btn-primary"
            data-testid="public-entry-back-to-pricing"
          >
            Back to pricing →
          </a>
          <p className="mt-6 text-xs text-black/50">
            Already have an account?{" "}
            <a href="/auth" className="underline font-semibold">
              Sign in
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (checkoutError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="public-entry-error">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="font-heading uppercase text-2xl mb-3">Something went wrong.</h1>
          <p className="text-black/70 mb-6 leading-relaxed">{checkoutError}</p>
          <a
            href={`${MARKETING_URL}/pricing${offer ? `?offer=${encodeURIComponent(offer)}` : ""}`}
            className="btn-primary"
          >
            Back to pricing →
          </a>
        </div>
      </div>
    );
  }

  // Loading / redirect-in-flight — brand-consistent minimal state.
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="text-center" data-testid="public-entry-loading">
        <div className="font-heading uppercase text-3xl tracking-wide">forecastly.</div>
        <div className="mt-4 text-sm text-black/60">
          {kind ? "Taking you to secure checkout…" : "One moment…"}
        </div>
      </div>
    </div>
  );
}
